import * as i0 from '@angular/core';
import { InjectionToken, Component, HostBinding, Input, ViewContainerRef, ViewChildren, EventEmitter, Output, Injectable, HostListener, Inject, Optional, NgModule } from '@angular/core';
import * as i1$1 from '@anthem/uxd/util';
import { UxEvents } from '@anthem/uxd/util';
import * as i1 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import { PortalModule } from '@angular/cdk/portal';
import { trigger, state, style, transition, animate, keyframes } from '@angular/animations';

class AlertRegion {
    constructor(_document) {
        this._document = _document;
        this._alerts = [];
    }
    _savePreviouslyFocusedElement() {
        const activeElement = this._document.activeElement;
        this._previouslyFocusedElement = activeElement;
    }
    _queue(alert) {
        this._alerts.unshift(alert);
        return this._alerts.slice();
    }
    _dequeue() {
        return this._alerts.pop();
    }
    _stack(alert) {
        this._alerts.push(alert);
        return this._alerts.slice();
    }
    _pop() {
        return this._alerts.shift();
    }
}
var AlertType;
(function (AlertType) {
    AlertType["WARNING"] = "warning";
    AlertType["INFO"] = "info";
    AlertType["SUCCESS"] = "success";
    AlertType["ERROR"] = "error";
})(AlertType || (AlertType = {}));
var GlobalPosition;
(function (GlobalPosition) {
    GlobalPosition["STATIC"] = "static";
    GlobalPosition["FIXED"] = "fixed";
})(GlobalPosition || (GlobalPosition = {}));
var UniversalAlertType;
(function (UniversalAlertType) {
    UniversalAlertType["UNIVERSAL"] = "universalInlineAlert";
})(UniversalAlertType || (UniversalAlertType = {}));
class Alert {
    get id() {
        return this._id;
    }
    constructor(_alert) {
        this._alert = _alert;
        this.type = AlertType.INFO;
        this.isDissmisable = false;
        const { type, data, isDissmisable, id } = _alert;
        this.data = data;
        this._id = id;
        this.type = type || this.type;
        this.isDissmisable = isDissmisable || this.isDissmisable;
    }
}
const ALERT_OPTIONS = new InjectionToken('AlertData');
const DEFAULT_ALERT_OPTIONS = new InjectionToken('default-alert-options', {
    providedIn: 'root',
    factory: ALERT_DEFAULT_OPTIONS_FACTORY
});
function ALERT_DEFAULT_OPTIONS_FACTORY() {
    return new AlertConfig();
}
class AlertConfig {
    constructor() {
        /** The politeness level for the alert announcement. */
        this.politeness = 'assertive';
        /** Data being injected into the child component. */
        this.data = null;
        this.alertListType = 'stack';
    }
}

//istanbul ignore next
class AlertComponentV2 {
    constructor() {
        this.alertRole = 'alert';
    }
    getAntAlertClass(alertType) {
        const negativeCheck = alertType === 'negative' || alertType === 'disaster';
        const positiveCheck = alertType === 'positive';
        return {
            'ant-negative': negativeCheck,
            'ant-positive': positiveCheck
        };
    }
    getAntAlertIconClass(alertType) {
        return {
            'alert-icon-positive': alertType === 'positive',
            'alert-icon-negative': alertType === 'negative',
            'alert-icon-info': alertType === 'information',
            'alert-icon-disaster': alertType === 'disaster'
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertComponentV2, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AlertComponentV2, selector: "[data-uxd-alert-cmp-v2]", inputs: { alert: "alert" }, host: { properties: { "attr.role": "this.alertRole" } }, ngImport: i0, template: `<div class="ant-alert" [ngClass]="getAntAlertClass(alert.alertType)">
              <div class="ant-alert-left">
                <span class="alert-icon" [ngClass]="getAntAlertIconClass(alert.alertType)" ></span>
              </div>
              <div class="ant-alert-main">
                <p>
                  <span #transclude>
                    <ng-content></ng-content>
                  </span>
                  <span *ngIf="transclude.children.length == 0" [innerHTML]="alert?.alertContent"> </span>
                  </p>
              </div>
            </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertComponentV2, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-alert-cmp-v2]',
                    template: `<div class="ant-alert" [ngClass]="getAntAlertClass(alert.alertType)">
              <div class="ant-alert-left">
                <span class="alert-icon" [ngClass]="getAntAlertIconClass(alert.alertType)" ></span>
              </div>
              <div class="ant-alert-main">
                <p>
                  <span #transclude>
                    <ng-content></ng-content>
                  </span>
                  <span *ngIf="transclude.children.length == 0" [innerHTML]="alert?.alertContent"> </span>
                  </p>
              </div>
            </div>`
                }]
        }], propDecorators: { alertRole: [{
                type: HostBinding,
                args: ['attr.role']
            }], alert: [{
                type: Input
            }] } });

//istanbul ignore next
class DissmisableAlertComponent {
    constructor(_uxHlpr) {
        this._uxHlpr = _uxHlpr;
        this._alertEvent = this._uxHlpr.getUxEvents(UxEvents[UxEvents.DISMISSABLE_ALERT]);
        this.alertOpened = false;
        this._alertTimer = null;
    }
    /**
     * sets 'this._subscription1' to the dissmisable alert global variable
     * shows or hides the dissmisable alert based on the eventype sent: OPEN | CLOSE
     */
    ngOnInit() {
        this._subscription1 = this._alertEvent.subscribe((e) => {
            if (e.eventType === 'OPEN') {
                this.showAlert(e);
            }
            else {
                this.closeAlert();
            }
        });
    }
    ngOnDestroy() {
        this._subscription1.unsubscribe();
    }
    /**
     * opens alert
     * closes any existing dissmisable alerts
     * sets alert variables from obj passed
     * sets focus to close button inside alert
     * automactically will hide alert after x milliseconds
     */
    showAlert(alert) {
        this.closeAlert(); // close+reset any existing alert
        if (document.activeElement) {
            this._prevFocusElem = document.activeElement; // previous active elem
        }
        this.alertType = alert.type;
        this.content = alert.content;
        this.alertOpened = true;
        setTimeout(() => {
            this._alertCloseBtn.first.element.nativeElement.focus();
        }, 100);
        this._alertTimer = setTimeout(() => {
            this.closeAlert();
        }, alert.timeout || 4000);
    }
    /**
     * closes alert
     * refocuses previous element
     * sets 'bool alertOpened' to false
     * resets content string to empty
     * sets 'alertTimer' to null and clears timeout
     */
    closeAlert() {
        if (this._prevFocusElem) {
            this._prevFocusElem.focus();
            this._prevFocusElem = null;
        }
        this.alertOpened = false;
        this.content = '';
        if (this._alertTimer) {
            clearTimeout(this._alertTimer);
            this._alertTimer = null;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DissmisableAlertComponent, deps: [{ token: i1$1.UxHelper }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: DissmisableAlertComponent, selector: "[data-uxd-dismissable-alert-cmp]", viewQueries: [{ propertyName: "_alertCloseBtn", predicate: ["alertCloseBtn"], descendants: true, read: ViewContainerRef }], ngImport: i0, template: `
            <div class="ant-alert ant-dismissable"
              role="alert" aria-live="assertive"
              [ngClass]="{'on': (alertOpened), 'ant-negative': (alertType === 'negative'), 'ant-positive': (alertType === 'positive'), 'ant-info': (alertType === 'info')}">
              <div class="media">
                <div class="media-left media-middle">
                  <span class="fa" aria-hidden="true"
                  [ngClass]="{'fa-exclamation': (alertType === 'negative'), 'fa-check': (alertType === 'positive'), 'fa-info': (alertType === 'neutral' || alertType === 'information')}"></span>
                </div>
                <div class="media-body media-middle">
                  <a #alertCloseBtn href="javascript:void(0)" (click)="closeAlert()" class="ant-dismiss-alert" role="button" name="close" aria-describedby="dismissAlertContent" aria-live="polite" aria-atomic="true"><span class="fa fa-remove"></span></a>
                  <p [innerHTML]="content" id="dismissAlertContent"></p>
                </div>
              </div>
            </div>
          `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DissmisableAlertComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-dismissable-alert-cmp]',
                    template: `
            <div class="ant-alert ant-dismissable"
              role="alert" aria-live="assertive"
              [ngClass]="{'on': (alertOpened), 'ant-negative': (alertType === 'negative'), 'ant-positive': (alertType === 'positive'), 'ant-info': (alertType === 'info')}">
              <div class="media">
                <div class="media-left media-middle">
                  <span class="fa" aria-hidden="true"
                  [ngClass]="{'fa-exclamation': (alertType === 'negative'), 'fa-check': (alertType === 'positive'), 'fa-info': (alertType === 'neutral' || alertType === 'information')}"></span>
                </div>
                <div class="media-body media-middle">
                  <a #alertCloseBtn href="javascript:void(0)" (click)="closeAlert()" class="ant-dismiss-alert" role="button" name="close" aria-describedby="dismissAlertContent" aria-live="polite" aria-atomic="true"><span class="fa fa-remove"></span></a>
                  <p [innerHTML]="content" id="dismissAlertContent"></p>
                </div>
              </div>
            </div>
          `
                }]
        }], ctorParameters: function () { return [{ type: i1$1.UxHelper }]; }, propDecorators: { _alertCloseBtn: [{
                type: ViewChildren,
                args: ['alertCloseBtn', { read: ViewContainerRef }]
            }] } });

let nextUuid$1 = 0;
//istanbul ignore next
class SimpleInlineAlertComponent {
    isDismissible() {
        return this.alert.isDissmisable;
    }
    // tslint:disable-next-line:no-empty
    constructor() {
        this._id = `inline-alert-${nextUuid$1++}`;
        this.onDismiss = new EventEmitter();
    }
    ngOnInit() {
        this._id = this.alert.id || this._id;
        //Flag to check type of data.message so view toggles correct section
        this.isTemplateRef = typeof this.alert.data.message === 'string' ? false : true;
        this.alertMessageTemplate = this.isTemplateRef === true ? this.alert.data.message : null;
    }
    dismiss() {
        this.onDismiss.emit();
    }
    alertCssClass() {
        const cssClass = `ant-alert-${this.alert.type}`;
        return cssClass;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SimpleInlineAlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SimpleInlineAlertComponent, selector: "[data-uxd-inline-alert-cmp]", inputs: { alert: "alert" }, outputs: { onDismiss: "onDismiss" }, ngImport: i0, template: `
  <div class="ant-inline-alert" [ngClass]="alertCssClass()" [id]="_id">
    <div class="ant-inline-alert-body">
      <span class="alert-icon" aria-hidden="true"></span>
      <span *ngIf="!isTemplateRef"  class="alert-message" [id]="_id + '-message'">{{ alert?.data?.message }}</span>
      <ng-container *ngIf="isTemplateRef" [ngTemplateOutlet]="alertMessageTemplate"></ng-container>
      <button *ngIf="isDismissible()" type="button" [id]="_id + '-dismiss'" class="dismiss-button" aria-label="Dismiss alert" (click)="dismiss()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SimpleInlineAlertComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-inline-alert-cmp]',
                    template: `
  <div class="ant-inline-alert" [ngClass]="alertCssClass()" [id]="_id">
    <div class="ant-inline-alert-body">
      <span class="alert-icon" aria-hidden="true"></span>
      <span *ngIf="!isTemplateRef"  class="alert-message" [id]="_id + '-message'">{{ alert?.data?.message }}</span>
      <ng-container *ngIf="isTemplateRef" [ngTemplateOutlet]="alertMessageTemplate"></ng-container>
      <button *ngIf="isDismissible()" type="button" [id]="_id + '-dismiss'" class="dismiss-button" aria-label="Dismiss alert" (click)="dismiss()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>`
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { alert: [{
                type: Input
            }], onDismiss: [{
                type: Output
            }] } });

//istanbul ignore next
class AlertHelper {
    constructor(_uxHlpr) {
        this._uxHlpr = _uxHlpr;
        this._alertEvent = this._uxHlpr.getUxEvents(UxEvents[UxEvents.DISMISSABLE_ALERT]);
    }
    openAlert(content, alertType, timeout = 4000) {
        this._alertEvent.emit({
            eventType: 'OPEN',
            type: alertType,
            content: content,
            timeout: timeout
        });
    }
    /**
     * closes the alert by emitting this._alertEvent which
     * should be subscribed to
     */
    closeAlert() {
        this._alertEvent.emit({
            eventType: 'CLOSE',
            type: '',
            content: ''
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertHelper, deps: [{ token: i1$1.UxHelper }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertHelper }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertHelper, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.UxHelper }]; } });

let nextUuid = 0;
//istanbul ignore next
class SnackbarAlertComponent extends SimpleInlineAlertComponent {
    alertMouseenter() {
        this.onMouseenter.emit(this.alert);
    }
    alertMouseleave() {
        this.onMouseleave.emit(this.alert);
    }
    constructor() {
        super();
        this.onMouseenter = new EventEmitter();
        this.onMouseleave = new EventEmitter();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SnackbarAlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SnackbarAlertComponent, selector: "[data-uxd-snackbar-alert-cmp]", outputs: { onMouseenter: "onMouseenter", onMouseleave: "onMouseleave" }, host: { listeners: { "mouseenter": "alertMouseenter()", "mouseleave": "alertMouseleave()" } }, usesInheritance: true, ngImport: i0, template: `
  <div class="ant-inline-alert ant-snackbar-alert" [ngClass]="alertCssClass()" [id]="_id">
    <div class="ant-inline-alert-body ant-snackbar-alert-body">
      <span *ngIf="!isTemplateRef" class="alert-message" [id]="_id + '-message'">{{ alert?.data?.message }}</span>
      <ng-container *ngIf="isTemplateRef" [ngTemplateOutlet]="alertMessageTemplate"></ng-container>
      <button *ngIf="isDismissible()" type="button" [id]="_id + '-dismiss'" class="dismiss-button" aria-label="Dismiss alert" (click)="dismiss()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SnackbarAlertComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-snackbar-alert-cmp]',
                    template: `
  <div class="ant-inline-alert ant-snackbar-alert" [ngClass]="alertCssClass()" [id]="_id">
    <div class="ant-inline-alert-body ant-snackbar-alert-body">
      <span *ngIf="!isTemplateRef" class="alert-message" [id]="_id + '-message'">{{ alert?.data?.message }}</span>
      <ng-container *ngIf="isTemplateRef" [ngTemplateOutlet]="alertMessageTemplate"></ng-container>
      <button *ngIf="isDismissible()" type="button" [id]="_id + '-dismiss'" class="dismiss-button" aria-label="Dismiss alert" (click)="dismiss()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>
  </div>`
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { onMouseenter: [{
                type: Output
            }], onMouseleave: [{
                type: Output
            }], alertMouseenter: [{
                type: HostListener,
                args: ['mouseenter']
            }], alertMouseleave: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

class AlertService {
    constructor(_injector, _factoryResolver, _defaultConfig) {
        this._injector = _injector;
        this._factoryResolver = _factoryResolver;
        this._defaultConfig = _defaultConfig;
        this._alertRegions = new Map();
    }
    _getAlertRegion(regionName) {
        let alertRegion = null;
        if (this._alertRegions.has(regionName)) {
            alertRegion = this._alertRegions.get(regionName);
        }
        return alertRegion;
    }
    _registerAlertRegion(regionName, alertRegion) {
        if (alertRegion) {
            this._alertRegions.set(regionName, alertRegion);
        }
    }
    _deregisterAlertRegion(regionName) {
        if (this._alertRegions.has(regionName)) {
            this._alertRegions.delete(regionName);
        }
    }
    warn(message, config) {
        this._alert(AlertType.WARNING, message, config);
    }
    info(message, config) {
        this._alert(AlertType.INFO, message, config);
    }
    success(message, config) {
        this._alert(AlertType.SUCCESS, message, config);
    }
    error(message, config) {
        this._alert(AlertType.ERROR, message, config);
    }
    _alert(type, message, config) {
        if (!this._alertRegions) {
            throw new Error(`No AlertRegion found`);
        }
        const _config = { ...this._defaultConfig, ...config };
        _config.data = {
            type,
            isDissmisable: config.isDissmisable,
            data: {
                message
            },
        };
        const alertRegionToAttach = this._getAlertRegion(_config.regionName);
        alertRegionToAttach.notify(new Alert(_config.data), _config);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertService, deps: [{ token: i0.Injector }, { token: i0.ComponentFactoryResolver }, { token: DEFAULT_ALERT_OPTIONS }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.ComponentFactoryResolver }, { type: AlertConfig, decorators: [{
                    type: Inject,
                    args: [DEFAULT_ALERT_OPTIONS]
                }] }]; } });

class NoAlertProviderForInlineAlert extends Error {
    constructor() {
        super(`No provider was found for Alert.`);
    }
}
// DEPRECATED: 'inline-alert-container'
class InlineAlertContainerComponent extends AlertRegion {
    get alerts() {
        return this._alerts;
    }
    constructor(_changeDetector, _alertService, _document) {
        super(_document);
        this._changeDetector = _changeDetector;
        this._alertService = _alertService;
        this.alertType = '';
        this.isStatic = false;
        if (!_alertService) {
            throw new NoAlertProviderForInlineAlert();
        }
    }
    ngOnInit() {
        // set css position to static per current design
        this.isStatic = this.alertType === UniversalAlertType.UNIVERSAL;
    }
    ngAfterViewInit() {
        this._alertService._registerAlertRegion(this.name, this);
    }
    ngOnDestroy() {
        this._alertService._deregisterAlertRegion(this.name);
    }
    notify(alert, config) {
        this._ariaLivePoliteness = config.politeness;
        if (config.alertListType === 'queue') {
            this._queue(alert);
        }
        else {
            this._stack(alert);
        }
    }
    dismissAlert(alert) {
        this._alerts = this._alerts.filter((value, index) => value !== alert);
        this._changeDetector.detectChanges();
    }
    dismissAlertId(id) {
        this._alerts = this._alerts.filter((_, index) => index !== id);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InlineAlertContainerComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: AlertService, optional: true }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: InlineAlertContainerComponent, selector: "inline-alert-container, ant-inline-alert", inputs: { name: "name", alertType: "alertType" }, host: { properties: { "class.universalInlineAlert": "this.isStatic" } }, usesInheritance: true, ngImport: i0, template: "<div class=\"ant-inline-alert-container\" aria-atomic=\"true\" [attr.aria-live]=\"_ariaLivePoliteness\">\n  <ul class=\"inline-alert-list\">\n    <li class=\"inline-alert-item\" *ngFor=\"let alert of alerts\">\n      <div data-uxd-inline-alert-cmp [alert]=\"alert\" (onDismiss)=\"dismissAlert(alert)\"></div>\n    </li>\n  </ul>\n</div>", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: SimpleInlineAlertComponent, selector: "[data-uxd-inline-alert-cmp]", inputs: ["alert"], outputs: ["onDismiss"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: InlineAlertContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'inline-alert-container, ant-inline-alert', template: "<div class=\"ant-inline-alert-container\" aria-atomic=\"true\" [attr.aria-live]=\"_ariaLivePoliteness\">\n  <ul class=\"inline-alert-list\">\n    <li class=\"inline-alert-item\" *ngFor=\"let alert of alerts\">\n      <div data-uxd-inline-alert-cmp [alert]=\"alert\" (onDismiss)=\"dismissAlert(alert)\"></div>\n    </li>\n  </ul>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: AlertService, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { name: [{
                type: Input
            }], alertType: [{
                type: Input
            }], isStatic: [{
                type: HostBinding,
                args: ['class.universalInlineAlert']
            }] } });

class GlobalAlertComponent {
    isDismissible() {
        return this.alert.isDissmisable;
    }
    // tslint:disable-next-line:no-empty
    constructor() {
        this.onDismiss = new EventEmitter();
    }
    // tslint:disable-next-line:no-empty
    ngOnInit() { }
    dismiss() {
        this.onDismiss.emit();
    }
    alertCssClass() {
        const cssClass = `ant-alert-${this.alert.type}`;
        return cssClass;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GlobalAlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: GlobalAlertComponent, selector: "global-alert", inputs: { alert: "alert" }, outputs: { onDismiss: "onDismiss" }, ngImport: i0, template: `
  <div class="ant-global-alert ant-global-alert--light" [ngClass]="alertCssClass()">
    <div class="ant-global-alert-body">
      <span class="alert-icon" aria-hidden="true"></span>
      <span class="alert-message">{{ alert?.data?.message }}</span>
      <button *ngIf="isDismissible" type="button" class="dismiss-button" aria-label="Dismiss alert" (click)="dismiss()">
        <span aria-hidden="true" class="alert-icon-close">
        </span>
      </button>
    </div>
  </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GlobalAlertComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'global-alert',
                    template: `
  <div class="ant-global-alert ant-global-alert--light" [ngClass]="alertCssClass()">
    <div class="ant-global-alert-body">
      <span class="alert-icon" aria-hidden="true"></span>
      <span class="alert-message">{{ alert?.data?.message }}</span>
      <button *ngIf="isDismissible" type="button" class="dismiss-button" aria-label="Dismiss alert" (click)="dismiss()">
        <span aria-hidden="true" class="alert-icon-close">
        </span>
      </button>
    </div>
  </div>`
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { alert: [{
                type: Input
            }], onDismiss: [{
                type: Output
            }] } });

class GlobalAlertContainerComponent extends AlertRegion {
    get alerts() {
        return this._alerts;
    }
    constructor(_alertService, _document) {
        super(_document);
        this._alertService = _alertService;
        // Set global position to fixed by default to support older versions
        this.position = GlobalPosition.FIXED;
        this.isStatic = false;
    }
    ngAfterViewInit() {
        this._alertService._registerAlertRegion('global', this);
    }
    // tslint:disable-next-line:no-empty
    ngOnInit() {
        // set css position to static per current design
        this.isStatic = this.position === GlobalPosition.STATIC;
    }
    notify(alert, config) {
        this._ariaLivePoliteness = config.politeness;
        if (config.alertListType === 'queue') {
            this._queue(alert);
        }
        else {
            this._stack(alert);
        }
    }
    dismissAlert(alert) {
        this._alerts = this._alerts.filter((value, index) => value !== alert);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GlobalAlertContainerComponent, deps: [{ token: AlertService, optional: true }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: GlobalAlertContainerComponent, selector: "global-alert-container", inputs: { position: "position" }, host: { properties: { "class.static": "this.isStatic" }, classAttribute: "ant-global-alert-container" }, usesInheritance: true, ngImport: i0, template: "<ul class=\"global-alert-list\" aria-atomic=\"true\" [attr.aria-live]=\"_ariaLivePoliteness\">\n  <li class=\"global-alert-item\" *ngFor=\"let alert of alerts\">\n    <!-- Deprecated Design -->\n    <global-alert [alert]=\"alert\" (onDismiss)=\"dismissAlert(alert)\" *ngIf=\"position === 'fixed'\"></global-alert>\n    <!-- Current Design -->\n    <div data-uxd-inline-alert-cmp [alert]=\"alert\" (onDismiss)=\"dismissAlert(alert)\" *ngIf=\"position === 'static'\">\n    </div>\n  </li>\n</ul>", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SimpleInlineAlertComponent, selector: "[data-uxd-inline-alert-cmp]", inputs: ["alert"], outputs: ["onDismiss"] }, { kind: "component", type: GlobalAlertComponent, selector: "global-alert", inputs: ["alert"], outputs: ["onDismiss"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: GlobalAlertContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'global-alert-container', host: {
                        class: 'ant-global-alert-container'
                    }, template: "<ul class=\"global-alert-list\" aria-atomic=\"true\" [attr.aria-live]=\"_ariaLivePoliteness\">\n  <li class=\"global-alert-item\" *ngFor=\"let alert of alerts\">\n    <!-- Deprecated Design -->\n    <global-alert [alert]=\"alert\" (onDismiss)=\"dismissAlert(alert)\" *ngIf=\"position === 'fixed'\"></global-alert>\n    <!-- Current Design -->\n    <div data-uxd-inline-alert-cmp [alert]=\"alert\" (onDismiss)=\"dismissAlert(alert)\" *ngIf=\"position === 'static'\">\n    </div>\n  </li>\n</ul>" }]
        }], ctorParameters: function () { return [{ type: AlertService, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { position: [{
                type: Input
            }], isStatic: [{
                type: HostBinding,
                args: ['class.static']
            }] } });

/**
 * Animations used by SnackbarAlertComponent.
 */
const antSnackbarAnimation = {
    /** Animation that transitions a snackbar alert in and out of view. */
    snackbarState: trigger('state', [
        state('visible', style({ transform: 'scale(1)' })),
        transition('void => *', animate('500ms cubic-bezier(0, 0, 0.2, 1)', keyframes([style({ opacity: 0, transform: 'scale(0)', offset: 0 }), style({ opacity: 0.5, transform: 'scale(0.99)', offset: 0.5 }), style({ opacity: 1, transform: 'scale(1)', offset: 1 })]))),
        transition('* => void', animate('500ms cubic-bezier(0, 0, 0.2, 1)', keyframes([style({ opacity: 1, offset: 0 }), style({ opacity: 0.5, offset: 0.5 }), style({ opacity: 0, offset: 1 })])))
    ])
};

class SnackbarAlertContainerComponent extends GlobalAlertContainerComponent {
    constructor(_alertService, _document) {
        super(_alertService, _document);
        this.animationState = 'visible';
        this.deleteAllInterval = false;
    }
    get alerts() {
        this.alertCheck();
        return this._alerts;
    }
    alertCheck() {
        if (this._alerts.length > 0) {
            let currentLength = this._alerts.length;
            if (currentLength >= this.prevLength) {
                const alertsToBeDismissed = [...this._alerts];
                let alert = alertsToBeDismissed.shift();
                this.delayDismiss(alert);
            }
        }
        else {
            this.deleteAllInterval = false;
        }
        this.prevLength = this._alerts.length;
    }
    dismissClick(i) {
        this.isHovering = false;
        let alertToBeDeleted = this._alerts[i];
        this.dismissAlert(alertToBeDeleted);
    }
    delayDismiss(alert) {
        this.delayTimeout = setTimeout(() => {
            if (!this.isHovering) {
                this.dismissAlert(alert);
            }
        }, 10000);
    }
    clearDismiss() {
        this.isHovering = true;
        if (this.delayTimeout) {
            clearTimeout(this.delayTimeout);
            this.delayTimeout = null;
        }
        setTimeout(() => {
            this.deleteAllInterval = true;
        }, 10000);
    }
    resumeDismiss() {
        this.isHovering = false;
        if (this.deleteAllInterval) {
            while (this._alerts.length) {
                this._alerts.pop();
            }
            clearTimeout(this.delayTimeout);
            this.delayTimeout = null;
        }
    }
    ngAfterViewInit() {
        this._alertService._registerAlertRegion('snackbar', this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SnackbarAlertContainerComponent, deps: [{ token: AlertService, optional: true }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SnackbarAlertContainerComponent, selector: "snackbar-alert-container", host: { classAttribute: "ant-snackbar-alert-container" }, usesInheritance: true, ngImport: i0, template: "<ul class=\"snackbar-alert-list\" aria-atomic=\"true\" [attr.aria-live]=\"_ariaLivePoliteness\">\n  <ng-container *ngFor=\"let alert of alerts; let i = index\">\n    <li [@state]=\"animationState\" class=\"global-alert-item snackbar-alert-item\">\n      <div data-uxd-snackbar-alert-cmp [alert]=\"alert\" (onMouseenter)=\"clearDismiss()\" (onMouseleave)=\"resumeDismiss()\" (onDismiss)=\"dismissClick(i)\"></div>\n    </li>\n  </ng-container>\n</ul>", dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "component", type: SnackbarAlertComponent, selector: "[data-uxd-snackbar-alert-cmp]", outputs: ["onMouseenter", "onMouseleave"] }], animations: [antSnackbarAnimation.snackbarState] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SnackbarAlertContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'snackbar-alert-container', host: {
                        class: 'ant-snackbar-alert-container'
                    }, animations: [antSnackbarAnimation.snackbarState], template: "<ul class=\"snackbar-alert-list\" aria-atomic=\"true\" [attr.aria-live]=\"_ariaLivePoliteness\">\n  <ng-container *ngFor=\"let alert of alerts; let i = index\">\n    <li [@state]=\"animationState\" class=\"global-alert-item snackbar-alert-item\">\n      <div data-uxd-snackbar-alert-cmp [alert]=\"alert\" (onMouseenter)=\"clearDismiss()\" (onMouseleave)=\"resumeDismiss()\" (onDismiss)=\"dismissClick(i)\"></div>\n    </li>\n  </ng-container>\n</ul>" }]
        }], ctorParameters: function () { return [{ type: AlertService, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

class AlertModule {
    static forRoot() {
        return {
            ngModule: AlertModule,
            providers: []
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: AlertModule, declarations: [AlertComponentV2, DissmisableAlertComponent, SimpleInlineAlertComponent, InlineAlertContainerComponent, GlobalAlertComponent, GlobalAlertContainerComponent, SnackbarAlertContainerComponent, SnackbarAlertComponent], imports: [CommonModule, PortalModule], exports: [AlertComponentV2, DissmisableAlertComponent, SimpleInlineAlertComponent, InlineAlertContainerComponent, GlobalAlertComponent, GlobalAlertContainerComponent, SnackbarAlertContainerComponent, SnackbarAlertComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertModule, imports: [CommonModule, PortalModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PortalModule],
                    declarations: [AlertComponentV2, DissmisableAlertComponent, SimpleInlineAlertComponent, InlineAlertContainerComponent, GlobalAlertComponent, GlobalAlertContainerComponent, SnackbarAlertContainerComponent, SnackbarAlertComponent],
                    exports: [AlertComponentV2, DissmisableAlertComponent, SimpleInlineAlertComponent, InlineAlertContainerComponent, GlobalAlertComponent, GlobalAlertContainerComponent, SnackbarAlertContainerComponent, SnackbarAlertComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ALERT_DEFAULT_OPTIONS_FACTORY, ALERT_OPTIONS, Alert, AlertComponentV2, AlertConfig, AlertHelper, AlertModule, AlertRegion, AlertService, AlertType, DEFAULT_ALERT_OPTIONS, DissmisableAlertComponent, GlobalAlertComponent, GlobalAlertContainerComponent, GlobalPosition, InlineAlertContainerComponent, NoAlertProviderForInlineAlert, SimpleInlineAlertComponent, SnackbarAlertComponent, SnackbarAlertContainerComponent, UniversalAlertType };
